import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { ShoppingCart, Plus, Minus, Search, ExternalLink, Package, Download, Share2, MessageCircle, Mail } from 'lucide-react';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { generateCatalogPDF } from '../utils/pdfGenerator';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Brand configuration with colors
const BRANDS = [
  { id: 'all', name: 'TOATE', color: 'bg-slate-600' },
  { id: 'BAUDER', name: 'BAUDER', color: 'bg-orange-500' },
  { id: 'RAVAPROOF', name: 'RAVAPROOF', color: 'bg-blue-600' },
  { id: 'REVESTECH', name: 'REVESTECH', color: 'bg-orange-500' },
  { id: 'Elevate', name: 'ELEVATE EPDM', color: 'bg-emerald-600' },
];

const Shop = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const { cart, addToCart, removeFromCart, updateQuantity } = useCart();
  const [catalogLoading, setCatalogLoading] = useState(false);

  const getDateStr = () => {
    const d = new Date();
    return `${d.getDate().toString().padStart(2, '0')}.${(d.getMonth() + 1).toString().padStart(2, '0')}.${d.getFullYear()}`;
  };

  const handleDownloadCatalog = async () => {
    try {
      setCatalogLoading(true);
      const response = await axios.get(`${API}/products`);
      const allProducts = response.data;
      if (!allProducts.length) {
        toast.error('Nu există produse în catalog');
        return;
      }
      const doc = generateCatalogPDF(allProducts);
      doc.save(`Catalog_IzoMAG_${getDateStr()}.pdf`);
      toast.success('Catalogul a fost descărcat cu succes!');
    } catch (error) {
      console.error('Error generating catalog:', error);
      toast.error('Eroare la generarea catalogului');
    } finally {
      setCatalogLoading(false);
    }
  };

  const shopUrl = `${window.location.origin}/magazin`;
  const shareText = `Catalog materiale de constructii IzoMAG - preturi actualizate ${getDateStr()}. Descarca catalogul aici:`;

  const handleShareWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(`${shareText} ${shopUrl}`)}`, '_blank');
  };

  const handleShareEmail = () => {
    const subject = `Catalog IzoMAG - Materiale de Constructii (${getDateStr()})`;
    const body = `${shareText}\n\n${shopUrl}\n\nIzoMAG - La noi gasesti solutia corecta la pretul corect\nTel: 0755 898 253 | www.izomag.ro`;
    window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    filterProducts();
  }, [products, selectedBrand, selectedCategory, searchQuery]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
      // Extract unique categories
      const uniqueCategories = [...new Set(response.data.map(p => p.category))];
      setCategories(uniqueCategories);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Eroare la încărcarea produselor');
    } finally {
      setLoading(false);
    }
  };

  const filterProducts = () => {
    let filtered = products;
    
    // Filter by brand
    if (selectedBrand !== 'all') {
      filtered = filtered.filter(p => p.brand === selectedBrand);
    }
    
    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(p => p.category === selectedCategory);
    }
    
    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p => 
        p.name.toLowerCase().includes(query) || 
        (p.description && p.description.toLowerCase().includes(query)) ||
        (p.category && p.category.toLowerCase().includes(query)) ||
        (p.brand && p.brand.toLowerCase().includes(query))
      );
    }
    
    setFilteredProducts(filtered);
  };

  const getCartQuantity = (productId) => {
    const item = cart.find(i => i.id === productId);
    return item ? item.quantity : 0;
  };

  const handleAddToCart = (e, product) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product, 1);
    toast.success('Produs adăugat în coș');
  };

  const handleRemoveFromCart = (e, productId) => {
    e.preventDefault();
    e.stopPropagation();
    const item = cart.find(i => i.id === productId);
    if (item && item.quantity > 1) {
      updateQuantity(productId, item.quantity - 1);
    } else {
      removeFromCart(productId);
    }
  };

  const getCategoriesForBrand = () => {
    if (selectedBrand === 'all') return categories;
    return [...new Set(products.filter(p => p.brand === selectedBrand).map(p => p.category))];
  };

  const getProductCount = (brandId) => {
    if (brandId === 'all') return products.length;
    return products.filter(p => p.brand === brandId).length;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-lime-50" data-testid="shop-page">
      <SEO title="Magazin Materiale de Constructii" description="Catalog complet de materiale hidroizolante - membrane bituminoase, EPDM, accesorii. Branduri: Bauder, Ravaproof, Revestech. Preturi actualizate, livrare in Moldova." canonical="/magazin" />
      <Navbar />
      
      <div className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="shop-title">
              Magazin Materiale
            </h1>
            <p className="text-lg text-slate-600">Comandă materiale de calitate direct de la noi</p>
            <div className="mt-4 flex flex-wrap justify-center items-center gap-3">
              <Button
                onClick={handleDownloadCatalog}
                disabled={catalogLoading}
                className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-full font-bold transition-all transform hover:scale-105 shadow-lg"
                data-testid="download-catalog-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                {catalogLoading ? 'Se generează...' : 'Descarcă catalog produse'}
              </Button>
              <button
                onClick={handleShareWhatsApp}
                className="p-3 rounded-full bg-green-500 hover:bg-green-600 text-white shadow-lg transition-all transform hover:scale-105"
                title="Partajează pe WhatsApp"
                data-testid="share-whatsapp-btn"
              >
                <MessageCircle className="w-5 h-5" />
              </button>
              <button
                onClick={handleShareEmail}
                className="p-3 rounded-full bg-blue-500 hover:bg-blue-600 text-white shadow-lg transition-all transform hover:scale-105"
                title="Partajează prin Email"
                data-testid="share-email-btn"
              >
                <Mail className="w-5 h-5" />
              </button>
            </div>
          </motion.div>

          {/* Brand Filter Buttons */}
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="flex flex-wrap justify-center gap-3 mb-6"
          >
            {BRANDS.map(brand => (
              <button
                key={brand.id}
                onClick={() => {
                  setSelectedBrand(brand.id);
                  setSelectedCategory('all');
                }}
                className={`px-6 py-3 rounded-full font-bold text-sm transition-all transform hover:scale-105 ${
                  selectedBrand === brand.id
                    ? `${brand.color} text-white shadow-lg`
                    : 'bg-white text-slate-700 border-2 border-slate-200 hover:border-slate-300'
                }`}
                data-testid={`brand-filter-${brand.id}`}
              >
                {brand.name}
                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                  selectedBrand === brand.id ? 'bg-white/20' : 'bg-slate-100'
                }`}>
                  {getProductCount(brand.id)}
                </span>
              </button>
            ))}
          </motion.div>

          {/* Search and Category Filter */}
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="glass-card p-4 mb-8"
          >
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search Input */}
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                <Input
                  placeholder="Caută materiale după nume, categorie sau descriere..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 py-6 text-lg"
                  data-testid="search-input"
                />
              </div>
              
              {/* Category Filter */}
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 py-3 border rounded-lg bg-white text-slate-700 min-w-[200px]"
                data-testid="category-filter"
              >
                <option value="all">Toate categoriile</option>
                {getCategoriesForBrand().map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            
            {/* Active Filters Summary */}
            {(selectedBrand !== 'all' || selectedCategory !== 'all' || searchQuery) && (
              <div className="mt-4 flex flex-wrap items-center gap-2 text-sm">
                <span className="text-slate-500">Filtre active:</span>
                {selectedBrand !== 'all' && (
                  <span className="px-3 py-1 bg-cyan-100 text-cyan-700 rounded-full">
                    Brand: {selectedBrand}
                  </span>
                )}
                {selectedCategory !== 'all' && (
                  <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full">
                    {selectedCategory}
                  </span>
                )}
                {searchQuery && (
                  <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full">
                    "{searchQuery}"
                  </span>
                )}
                <button
                  onClick={() => {
                    setSelectedBrand('all');
                    setSelectedCategory('all');
                    setSearchQuery('');
                  }}
                  className="text-red-500 hover:text-red-700 underline"
                >
                  Resetează
                </button>
              </div>
            )}
          </motion.div>

          {/* Results Count */}
          <div className="mb-4 text-slate-600">
            {filteredProducts.length} {filteredProducts.length === 1 ? 'produs găsit' : 'produse găsite'}
          </div>

          {/* Products Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
            </div>
          ) : filteredProducts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredProducts.map((product, index) => {
                const cartQty = getCartQuantity(product.id);
                const hasMultipleImages = product.images && product.images.length > 1;
                return (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.03 }}
                  >
                    <Link 
                      to={`/produs/${product.id}`}
                      className="block glass-card overflow-hidden hover:shadow-xl transition-all duration-300 group"
                      data-testid={`product-${product.id}`}
                    >
                      <div className="relative overflow-hidden">
                        {/* Main Image with hover effect */}
                        <div className="relative w-full h-48">
                          <img
                            src={product.image_url}
                            alt={product.name}
                            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${hasMultipleImages ? 'group-hover:opacity-0' : 'group-hover:scale-105'}`}
                          />
                          {hasMultipleImages && (
                            <img
                              src={product.images[1]}
                              alt={`${product.name} - hover`}
                              className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                            />
                          )}
                        </div>
                        {/* Brand Badge */}
                        {product.brand && (
                          <span className={`absolute top-3 left-3 px-2 py-1 text-xs font-bold text-white rounded ${
                            product.brand === 'BAUDER' ? 'bg-orange-500' :
                            product.brand === 'RAVAPROOF' ? 'bg-blue-600' :
                            product.brand === 'REVESTECH' ? 'bg-orange-500' :
                            product.brand === 'Elevate' ? 'bg-emerald-600' :
                            'bg-slate-600'
                          }`}>
                            {product.brand}
                          </span>
                        )}
                        {/* Multiple images indicator */}
                        {hasMultipleImages && (
                          <div className="absolute bottom-3 right-3 bg-black/60 text-white text-xs px-2 py-1 rounded-full">
                            {product.images.length} poze
                          </div>
                        )}
                        {/* View Details Icon */}
                        <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                          <span className="bg-white/90 p-2 rounded-full shadow-lg flex items-center justify-center">
                            <ExternalLink className="w-4 h-4 text-slate-700" />
                          </span>
                        </div>
                      </div>
                      <div className="p-5">
                        <span className="text-xs text-cyan-600 font-medium uppercase tracking-wide">
                          {product.category}
                        </span>
                        <h3 className="text-lg font-bold text-slate-900 mb-2 line-clamp-2 group-hover:text-cyan-600 transition" style={{ fontFamily: 'Outfit, sans-serif' }}>
                          {product.name}
                        </h3>
                        {product.short_description && (
                          <p className="text-sm text-slate-500 mb-3 line-clamp-1">{product.short_description}</p>
                        )}
                        <div className="flex items-center justify-between mb-4">
                          <div className="text-2xl font-bold text-orange-500" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            {product.price.toFixed(2)} LEI
                          </div>
                          <div className="text-sm text-slate-500">/{product.unit}</div>
                        </div>
                        
                        {cartQty > 0 ? (
                          <div className="flex items-center justify-between" onClick={(e) => e.preventDefault()}>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => handleRemoveFromCart(e, product.id)}
                              data-testid={`remove-${product.id}`}
                            >
                              <Minus className="w-4 h-4" />
                            </Button>
                            <span className="font-bold text-lg" data-testid={`quantity-${product.id}`}>{cartQty}</span>
                            <Button
                              size="sm"
                              onClick={(e) => handleAddToCart(e, product)}
                              className="btn-primary"
                              data-testid={`add-more-${product.id}`}
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                        ) : (
                          <Button
                            onClick={(e) => handleAddToCart(e, product)}
                            className="btn-primary w-full"
                            data-testid={`add-to-cart-${product.id}`}
                          >
                            <ShoppingCart className="w-4 h-4 mr-2" /> Adaugă în Coș
                          </Button>
                        )}
                      </div>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-20">
              <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-lg text-slate-600 mb-4">Nu s-au găsit produse pentru criteriile selectate.</p>
              <Button
                variant="outline"
                onClick={() => {
                  setSelectedBrand('all');
                  setSelectedCategory('all');
                  setSearchQuery('');
                }}
              >
                Resetează filtrele
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Shop;
